// src/components/ProFeatureGate.js
// Blocks access to features based on tier hierarchy and shows upgrade prompt
import React, { useState } from 'react';
import { useSubscription, PRO_ONLY_FEATURES } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { createUpgradeSession } from '../services/stripe.service';
import { FEATURE_ACCESS, getTierById, getRequiredTierForFeature, isTierAtLeast } from '../config/pricing';
import { Crown, Lock, Loader2, Package, Archive, FileDown, BarChart3 } from 'lucide-react';
import { toast } from './Toast';

// Feature icons and descriptions
const FEATURE_INFO = {
  [PRO_ONLY_FEATURES.EXPORT]: {
    icon: FileDown,
    title: 'Export Data',
    description: 'Export your inventory, sales, and expenses to CSV or Excel files.',
  },
  [PRO_ONLY_FEATURES.PACKAGE_TRACKING]: {
    icon: Package,
    title: 'Package Tracking',
    description: 'Track incoming packages and shipments with real-time status updates.',
  },
  // PRO_ONLY_FEATURES.PRE_ORDERS entry removed 2026-06-13 — feature retired.
  [PRO_ONLY_FEATURES.VAULT]: {
    icon: Archive,
    title: 'Vault',
    description: 'Archive sold items and keep a permanent record of your sales history.',
  },
  [PRO_ONLY_FEATURES.REPORTS]: {
    icon: BarChart3,
    title: 'Reports & Analytics',
    description: 'Access detailed reports and analytics for your business.',
  },
  [PRO_ONLY_FEATURES.DATA_EXPORT]: {
    icon: FileDown,
    title: 'Account Data Export',
    description: 'Export all your account data for backup or migration.',
  },
};

// Get the required tier info for a feature
function getRequiredTierInfo(feature) {
  const requiredTierId = FEATURE_ACCESS[feature];
  if (!requiredTierId) return null;
  return getTierById(requiredTierId);
}

// Full page gate for tier-restricted pages (Vault, Reports, etc.)
export const ProFeaturePage = ({ feature, children }) => {
  const { canAccessFeature, subscriptionStatus } = useSubscription();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);

  // Users with sufficient tier see the actual content
  if (canAccessFeature(feature)) {
    return children;
  }

  const featureInfo = FEATURE_INFO[feature] || {
    icon: Lock,
    title: 'Pro Feature',
    description: 'This feature is available with a paid subscription.',
  };
  const FeatureIcon = featureInfo.icon;

  // Get required tier for this feature. Fallbacks updated 2026-05-30:
  // 'Pro' → 'Plus' (display name), $15 → $10 (monthly price), 'pro' stays
  // as the internal tier ID. The fallbacks only fire if the feature
  // isn't in FEATURE_ACCESS (edge case) — but when they do they should
  // reflect current pricing, not the old 4-tier copy.
  const requiredTier = getRequiredTierInfo(feature);
  const requiredTierName = requiredTier ? requiredTier.name : 'Plus';
  const requiredTierPrice = requiredTier ? requiredTier.monthlyPrice : 10;
  const requiredTierId = requiredTier ? requiredTier.id : 'pro';

  const handleUpgrade = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      const result = await createUpgradeSession(
        requiredTierId,
        'month'
      );

      if (result.error) {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-6">
          <FeatureIcon className="w-10 h-10 text-amber-400" />
        </div>

        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs mb-4 bg-amber-500/10 border border-amber-500/20 text-amber-400">
          <Crown className="w-3.5 h-3.5" />
          <span>Requires {requiredTierName}</span>
        </div>

        <h1 className="text-2xl font-bold text-zinc-900 dark:text-white mb-3">
          {featureInfo.title}
        </h1>

        <p className="text-zinc-600 dark:text-zinc-400 mb-8">
          {featureInfo.description}
        </p>

        <button
          onClick={handleUpgrade}
          disabled={loading}
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500
                   hover:from-amber-400 hover:to-orange-400 text-white font-semibold rounded-xl
                   transition-all disabled:opacity-50 shadow-lg shadow-amber-500/25"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Crown className="w-5 h-5" />
          )}
          Upgrade to {requiredTierName} - ${requiredTierPrice}/month
        </button>

        <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-500">
          Unlock {featureInfo.title.toLowerCase()} and more
        </p>
      </div>
    </div>
  );
};

// Inline gate for buttons/actions (shows lock icon and upgrade prompt on click)
export const ProFeatureButton = ({
  feature,
  children,
  onClick,
  className = '',
  disabled = false,
  ...props
}) => {
  const { canAccessFeature, subscriptionStatus } = useSubscription();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);

  // Users with sufficient tier get the normal button
  if (canAccessFeature(feature)) {
    return (
      <button onClick={onClick} className={className} disabled={disabled} {...props}>
        {children}
      </button>
    );
  }

  const requiredTier = getRequiredTierInfo(feature);
  const requiredTierId = requiredTier ? requiredTier.id : 'pro';

  const handleClick = async () => {
    setLoading(true);
    try {
      const result = await createUpgradeSession(
        requiredTierId,
        'month'
      );

      if (result.error) {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const featureInfo = FEATURE_INFO[feature];

  return (
    <button
      onClick={handleClick}
      className={`${className} relative`}
      disabled={loading}
      title={`Requires ${requiredTier?.name || 'Plus'}: ${featureInfo?.title || 'Upgrade to unlock'}`}
      {...props}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <>
          <Lock className="w-3.5 h-3.5 mr-1.5 inline" />
          {children}
        </>
      )}
    </button>
  );
};

// Simple check component - shows upgrade toast for free users
export const useProFeatureCheck = () => {
  const { canAccessFeature, subscriptionStatus } = useSubscription();
  const { currentUser } = useAuth();

  const checkAndPrompt = async (feature) => {
    if (canAccessFeature(feature)) {
      return true;
    }

    const featureInfo = FEATURE_INFO[feature];
    const requiredTier = getRequiredTierInfo(feature);
    // Same fallback update as ProFeaturePage above — 'Pro' → 'Plus', 15 → 10.
    const tierName = requiredTier ? requiredTier.name : 'Plus';
    const tierPrice = requiredTier ? requiredTier.monthlyPrice : 10;

    toast.error(
      `${featureInfo?.title || 'This feature'} requires ${tierName}. Upgrade for $${tierPrice}/month.`,
      { duration: 4000 }
    );
    return false;
  };

  return { checkAndPrompt, isPro: subscriptionStatus.isPro };
};

export default ProFeaturePage;
